/// <reference lib="dom" />
// ^ See shared/lib/pwa.web.ts for why the DOM lib is opted into per-file rather than
//   project-wide.

/**
 * Choosing a spreadsheet to upload — WEB build.
 *
 * A hidden `<input type="file">`, clicked programmatically. Two details that are the
 * whole reason this is not three lines inline:
 *
 *   - The input must be IN the document when clicked (Firefox ignores a click on a
 *     detached node) and removed afterwards, or every import leaves one behind.
 *   - `change` does not fire when the user cancels the OS dialog — there is no
 *     "cancelled" event in any browser. So the promise would hang forever on a cancel
 *     unless something else resolves it: `window.focus` returning to the page is the
 *     conventional signal, checked one turn later so a real selection wins the race.
 */
import type {ImportFile} from '@/entities/personnel/api';

/** What the roster import accepts, by extension and by MIME type. */
const ACCEPT =
  '.xlsx,.xls,.csv,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' +
  'application/vnd.ms-excel,text/csv';

export function pickSpreadsheet(): Promise<ImportFile | null> {
  return new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ACCEPT;
    input.style.display = 'none';

    let settled = false;
    const finish = (file: ImportFile | null) => {
      if (settled) {
        return;
      }
      settled = true;
      window.removeEventListener('focus', onFocus);
      input.remove();
      resolve(file);
    };

    const onFocus = () => {
      // The dialog closed. A real pick fires `change` right after this, so give it a
      // turn before concluding the user cancelled.
      setTimeout(() => finish(null), 300);
    };

    input.addEventListener('change', () => {
      finish(input.files?.[0] ?? null);
    });

    document.body.appendChild(input);
    window.addEventListener('focus', onFocus, {once: true});
    input.click();
  });
}

export const CAN_PICK_FILE = true;
